import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Trash2 } from "lucide-react";
import { AdminShell } from "../components/admin/AdminShell";
import { API, fmtPrice } from "../lib/eventUtils";

const STATUS_STYLE = {
  registered: "border-volt/60 text-volt",
  waiting: "border-amber-500/60 text-amber-400",
  notified: "border-sky-500/60 text-sky-400",
};

const EMPTY_FILTERS = { search: "", event_id: "", type: "", status: "", source: "", start: "", end: "" };

export default function AdminSignups() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");

  const params = useCallback(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  useEffect(() => {
    axios.get(`${API}/api/admin/events`, { withCredentials: true }).then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        setError("");
        const { data } = await axios.get(`${API}/api/admin/signups`, { params: params(), withCredentials: true });
        setRows(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Could not load signups");
      }
    }, 300);
    return () => clearTimeout(t);
  }, [params]);

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/signups/export`, { params: params(), withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "hyroxninja-signups.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const removeWaitlist = async (r) => {
    if (!window.confirm(`Remove ${r.first_name} ${r.last_name} from the "${r.event_title}" waitlist?`)) return;
    try {
      await axios.delete(`${API}/api/admin/waitlist/${r.id}`, { withCredentials: true });
      toast.success("Waitlist entry removed");
      setRows((prev) => prev.filter((x) => x.id !== r.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <AdminShell title="Signups">
      <div data-testid="admin-signups-page">
        <div className="flex flex-wrap items-end gap-3 mb-6" data-testid="signups-filters">
          <input value={filters.search} onChange={set("search")} placeholder="Search name or email…" className="volt-input !w-56" data-testid="signups-search" />
          <select value={filters.event_id} onChange={set("event_id")} className="volt-input !w-auto" data-testid="signups-filter-event">
            <option value="">All events</option>
            {events.map((ev) => <option key={ev.event_id} value={ev.event_id}>{ev.title}</option>)}
          </select>
          <select value={filters.type} onChange={set("type")} className="volt-input !w-auto" data-testid="signups-filter-type">
            <option value="">Registrations + Waitlist</option>
            <option value="registration">Registrations</option>
            <option value="waitlist">Waitlist</option>
          </select>
          <select value={filters.status} onChange={set("status")} className="volt-input !w-auto" data-testid="signups-filter-status">
            <option value="">All statuses</option>
            <option value="registered">Registered</option>
            <option value="waiting">Waiting</option>
            <option value="notified">Notified</option>
          </select>
          <select value={filters.source} onChange={set("source")} className="volt-input !w-auto" data-testid="signups-filter-source">
            <option value="">All sources</option>
            <option value="web">Web</option>
            <option value="import">Import</option>
          </select>
          <input type="date" value={filters.start} onChange={set("start")} className="volt-input !w-auto" data-testid="signups-filter-start" />
          <input type="date" value={filters.end} onChange={set("end")} className="volt-input !w-auto" data-testid="signups-filter-end" />
          <button onClick={exportCsv} className="btn-ghost !py-2.5 !px-4 !text-xs" data-testid="signups-export-button">
            <Download size={13} /> Export CSV
          </button>
        </div>

        {error && <p className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3 mb-6" data-testid="signups-error">{error}</p>}
        {rows === null && !error && <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-zinc-500 animate-pulse" data-testid="signups-loading">Loading…</p>}
        {rows !== null && rows.length === 0 && (
          <p className="text-zinc-500 text-sm border border-white/10 bg-[#0c0c0c] p-8" data-testid="signups-empty">No signups match these filters.</p>
        )}

        {rows !== null && rows.length > 0 && (
          <div className="overflow-x-auto border border-white/10 bg-[#0c0c0c]">
            <table className="w-full text-sm" data-testid="signups-table">
              <thead>
                <tr className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10 text-left">
                  <th className="py-2.5 px-4">Attendee</th>
                  <th className="py-2.5 pr-4">Contact</th>
                  <th className="py-2.5 pr-4">Event</th>
                  <th className="py-2.5 pr-4">Status</th>
                  <th className="py-2.5 pr-4">Source</th>
                  <th className="py-2.5 pr-4">Paid</th>
                  <th className="py-2.5 pr-4">Date</th>
                  <th className="py-2.5 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.type}-${r.id}`} className="border-b border-white/5 text-zinc-300" data-testid={`signups-row-${i}`}>
                    <td className="py-2.5 px-4 font-semibold text-white whitespace-nowrap">{r.first_name} {r.last_name}</td>
                    <td className="py-2.5 pr-4">
                      <p>{r.email}</p>
                      <p className="text-zinc-600 text-xs">{r.phone || ""}{r.instagram ? ` · @${r.instagram}` : ""}</p>
                    </td>
                    <td className="py-2.5 pr-4 text-zinc-400">{r.event_title}</td>
                    <td className="py-2.5 pr-4">
                      <span className={`font-mono2 text-[9px] uppercase tracking-[0.15em] border px-2 py-0.5 ${STATUS_STYLE[r.status] || "border-white/20 text-zinc-400"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">{r.source}</td>
                    <td className="py-2.5 pr-4 font-mono2 text-xs">
                      {r.amount_paid_cents != null && r.amount_paid_cents > 0 ? (
                        <span className="text-volt">{fmtPrice(r.amount_paid_cents)}{r.affiliate_code ? ` (${r.affiliate_code})` : ""}</span>
                      ) : r.affiliate_code ? (
                        <span className="text-volt">Free ({r.affiliate_code})</span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-zinc-500 text-xs whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="py-2.5 pr-4 text-right">
                      {r.type === "waitlist" && (
                        <button onClick={() => removeWaitlist(r)} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid={`signups-remove-waitlist-${i}`} title="Remove from waitlist">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
